/**
 * PDF Preview Component
 * Enhanced PDF viewer using react-pdf (PDF.js) for better rendering and controls
 * Similar to Overleaf's PDF viewer experience
 */

import React, { useState, useEffect, useCallback } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'
import {
  DocumentTextIcon,
  ArrowsPointingOutIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
  ArrowsPointingInIcon,
  InformationCircleIcon,
  SunIcon,
  MoonIcon
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { logDebug, logError } from '@utils/logger'
import { latexAPI } from '@api/latex'

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

interface PDFPreviewProps {
  pdfBlob?: Blob
  isLoading?: boolean
  error?: string
  className?: string
  onDownload?: () => void
  onOpenExternal?: () => void
  compilationErrors?: string[]
  compilationWarnings?: string[]
  onToggleDebugPanel?: () => void
  isDebugPanelOpen?: boolean
  navigateToPage?: number | null // Navigate to specific page
  documentId?: string // Document ID for SyncTeX lookup
  mainFilePath?: string // Main .tex file path for SyncTeX
  onNavigateToSource?: (filePath: string, line: number, column: number) => void // Callback when source location is found
}

const PDFPreview: React.FC<PDFPreviewProps> = ({
  pdfBlob,
  isLoading = false,
  error,
  className = '',
  onDownload,
  onOpenExternal,
  compilationErrors = [],
  compilationWarnings = [],
  onToggleDebugPanel,
  isDebugPanelOpen = false,
  navigateToPage = null,
  documentId,
  mainFilePath,
  onNavigateToSource
}) => {
  const [numPages, setNumPages] = useState<number>(0)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [scale, setScale] = useState<number>(1.0)
  const [pdfUrl, setPdfUrl] = useState<string>('')
  const [loadError, setLoadError] = useState<string>('')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [pageNumberOffset, setPageNumberOffset] = useState<number>(0) // Offset between viewer pages and printed page numbers
  const [pageDimensions, setPageDimensions] = useState<Map<number, { width: number; height: number }>>(new Map()) // Store PDF page dimensions in points

  // Track previous blob to detect changes
  const prevBlobRef = React.useRef<Blob | null>(null)
  
  // Ref to store scroll position before PDF changes
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null)
  const savedScrollPositionRef = React.useRef<{ pageNumber: number; scrollTop: number } | null>(null)
  const currentPageNumberRef = React.useRef<number>(1)
  
  // Update ref whenever pageNumber changes
  useEffect(() => {
    currentPageNumberRef.current = pageNumber
  }, [pageNumber])
  
  // Create object URL when PDF blob changes (for download/external open)
  // Use a ref to track the previous URL to properly clean it up
  const prevUrlRef = React.useRef<string>('')
  useEffect(() => {
    // Check if blob actually changed (by reference, size, or type)
    const blobChanged = !prevBlobRef.current || 
                       !pdfBlob || 
                       prevBlobRef.current !== pdfBlob ||
                       prevBlobRef.current.size !== pdfBlob.size ||
                       prevBlobRef.current.type !== pdfBlob.type
    
    if (blobChanged) {
      // Save current scroll position and page number before PDF changes
      if (scrollContainerRef.current && prevBlobRef.current) {
        const scrollTop = scrollContainerRef.current.scrollTop
        savedScrollPositionRef.current = {
          pageNumber: currentPageNumberRef.current,
          scrollTop
        }
        logDebug('[PDFPreview] Saved scroll position before PDF change:', {
          pageNumber: currentPageNumberRef.current,
          scrollTop
        })
      }
      
      // Revoke previous URL if it exists
      if (prevUrlRef.current) {
        URL.revokeObjectURL(prevUrlRef.current)
        prevUrlRef.current = ''
      }
      
      if (pdfBlob) {
        try {
          // Create URL for download/external open functionality
          const url = URL.createObjectURL(pdfBlob)
          setPdfUrl(url)
          setLoadError('')
          // Don't reset pageNumber - we'll restore it after PDF loads
          prevUrlRef.current = url
          prevBlobRef.current = pdfBlob
          logDebug('[PDFPreview] Created blob URL for PDF:', {
            url,
            blobSize: pdfBlob.size,
            blobType: pdfBlob.type
          })
        } catch (error) {
          logError('[PDFPreview] Failed to create blob URL:', error)
          setLoadError('Failed to create PDF preview')
          setPdfUrl('')
          prevBlobRef.current = null
        }
      } else {
        setPdfUrl('')
        setNumPages(0)
        setPageNumber(1)
        prevBlobRef.current = null
        savedScrollPositionRef.current = null
      }
    }
    
    // Cleanup URL when component unmounts
    return () => {
      if (prevUrlRef.current) {
        URL.revokeObjectURL(prevUrlRef.current)
        prevUrlRef.current = ''
      }
    }
  }, [pdfBlob])

  // Detect page number offset by scanning PDF pages for Arabic page numbers
  const detectPageNumberOffset = useCallback(async (pdfDocument: any, totalPages: number) => {
    try {
      logDebug('[PDFPreview] Detecting page number offset...')
      
      // Scan first 30 pages (or total pages if less) to find first Arabic page number
      const pagesToScan = Math.min(30, totalPages)
      let firstArabicPage = -1
      
      for (let i = 1; i <= pagesToScan; i++) {
        try {
          const page = await pdfDocument.getPage(i)
          const textContent = await page.getTextContent()
          
          // Look for Arabic numerals (1-9) that could be page numbers
          // Page numbers are typically at bottom center or corners
          const textItems = textContent.items || []
          
          for (const item of textItems) {
            if (item.str && /^\d+$/.test(item.str.trim())) {
              const pageNum = parseInt(item.str.trim(), 10)
              
              // Check if this looks like a page number (typically 1-1000 range, and in bottom area)
              // Page numbers are usually in the bottom 20% of the page
              const yPos = item.transform ? item.transform[5] : 0
              const pageHeight = page.view[3]
              const isInBottomArea = yPos < pageHeight * 0.2
              
              if (pageNum >= 1 && pageNum <= 1000 && isInBottomArea) {
                // Found what looks like the first Arabic page number
                if (firstArabicPage === -1 || pageNum === 1) {
                  firstArabicPage = i
                  logDebug('[PDFPreview] Found first Arabic page number:', {
                    viewerPage: i,
                    printedPageNumber: pageNum,
                    text: item.str,
                    yPosition: yPos,
                    pageHeight
                  })
                  
                  // If we found page "1", calculate offset
                  if (pageNum === 1) {
                    const offset = i - 1
                    setPageNumberOffset(offset)
                    logDebug('[PDFPreview] Page number offset detected:', offset)
                    return offset
                  }
                }
              }
            }
          }
        } catch (error) {
          logDebug('[PDFPreview] Error scanning page', i, ':', error)
          // Continue to next page
        }
      }
      
      // If we found a page but it wasn't page 1, estimate offset
      if (firstArabicPage > 0) {
        // This is a heuristic - if first Arabic page is at viewer page N and shows page number M,
        // offset is approximately N - M
        // But we don't know M, so we'll use firstArabicPage - 1 as a conservative estimate
        const estimatedOffset = firstArabicPage - 1
        setPageNumberOffset(estimatedOffset)
        logDebug('[PDFPreview] Estimated page number offset:', estimatedOffset, 'based on first Arabic page at viewer page', firstArabicPage)
        return estimatedOffset
      }
      
      logDebug('[PDFPreview] Could not detect page number offset, using 0')
      setPageNumberOffset(0)
      return 0
    } catch (error) {
      logError('[PDFPreview] Error detecting page number offset:', error)
      setPageNumberOffset(0)
      return 0
    }
  }, [])
  
  const onDocumentLoadSuccess = async (pdfInfo: { numPages: number }) => {
    const numPages = pdfInfo.numPages
    setNumPages(numPages)
    setLoadError('')
    
    // Detect page number offset when PDF loads
    // Access the PDF document from the Document component's ref or from pdfBlob
    if (pdfBlob) {
      try {
        const loadingTask = pdfjs.getDocument({ data: await pdfBlob.arrayBuffer() })
        const pdfDocument = await loadingTask.promise
        detectPageNumberOffset(pdfDocument, numPages).catch(error => {
          logError('[PDFPreview] Failed to detect page offset:', error)
        })
      } catch (error) {
        logError('[PDFPreview] Failed to load PDF for offset detection:', error)
      }
    }
    
    // Restore scroll position after PDF loads
    if (savedScrollPositionRef.current) {
      const { pageNumber: savedPage, scrollTop: savedScrollTop } = savedScrollPositionRef.current
      // Clamp saved page number to valid range
      const validPage = Math.max(1, Math.min(savedPage, numPages))
      
      // Restore page number and scroll position after a short delay to ensure DOM is ready
      setTimeout(() => {
        setPageNumber(validPage)
        
        // Try to restore exact scroll position first
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = savedScrollTop
          
          // If exact scroll position doesn't work (e.g., page count changed), scroll to the page
          setTimeout(() => {
            const pageElement = document.querySelector(`[data-page-number="${validPage}"]`)
            if (pageElement && scrollContainerRef.current) {
              // Check if we're close to the saved position, if not, scroll to page
              const currentScrollTop = scrollContainerRef.current.scrollTop
              if (Math.abs(currentScrollTop - savedScrollTop) > 100) {
                pageElement.scrollIntoView({ behavior: 'auto', block: 'start' })
              }
            }
          }, 50)
        }
        
        logDebug('[PDFPreview] Restored scroll position after PDF load:', {
          pageNumber: validPage,
          scrollTop: savedScrollTop
        })
        
        // Clear saved position after restoring
        savedScrollPositionRef.current = null
      }, 100)
    }
  }
  
  // Navigate to specific page when navigateToPage prop changes
  useEffect(() => {
    logDebug('[PDFPreview] navigateToPage effect triggered:', { 
      navigateToPage, 
      numPages, 
      pageNumber,
      scrollContainerReady: !!scrollContainerRef.current 
    })
    
    if (navigateToPage !== null && navigateToPage > 0 && numPages > 0) {
      // Apply page number offset: warning page numbers refer to printed pages,
      // but viewer counts all pages including front matter
      const viewerPage = navigateToPage + pageNumberOffset
      const validPage = Math.max(1, Math.min(viewerPage, numPages))
      
      logDebug('[PDFPreview] Setting page number to:', validPage, 'from navigateToPage:', navigateToPage, 'offset:', pageNumberOffset)
      
      // Update the target page for scrolling
      const targetPageForScroll = validPage
      setPageNumber(validPage)
      
      // Scroll to the page after a delay to ensure DOM is ready
      // Use multiple attempts with increasing delays to handle async rendering
      const attemptScroll = (attempt: number = 0) => {
        logDebug('[PDFPreview] Attempt scroll, attempt:', attempt, 'target page:', targetPageForScroll)
        
        if (!scrollContainerRef.current) {
          if (attempt < 10) {
            setTimeout(() => attemptScroll(attempt + 1), 200)
          }
          return
        }
        
        // Search within the scroll container, not the entire document
        const pageElement = scrollContainerRef.current.querySelector(`[data-page-number="${targetPageForScroll}"]`) as HTMLElement
        
        if (pageElement) {
          // Calculate the position relative to the scroll container
          const containerRect = scrollContainerRef.current.getBoundingClientRect()
          const elementRect = pageElement.getBoundingClientRect()
          const scrollTop = scrollContainerRef.current.scrollTop
          const elementTop = elementRect.top - containerRect.top + scrollTop
          
          // Scroll to the element
          scrollContainerRef.current.scrollTo({
            top: elementTop - 20, // Add small offset from top
            behavior: 'smooth'
          })
          
          logDebug('[PDFPreview] Navigated to page:', { 
            page: targetPageForScroll, 
            scrollTop: elementTop,
            elementFound: true 
          })
        } else {
          // Retry if element not found (might still be rendering)
          if (attempt < 10) {
            setTimeout(() => attemptScroll(attempt + 1), 200)
          }
        }
      }
      
      // Start scrolling after initial delay
      setTimeout(() => attemptScroll(), 100)
    }
  }, [navigateToPage, numPages, pageNumberOffset])

  const onDocumentLoadError = (error: Error) => {
    logError('[PDFPreview] PDF load error:', error)
    setLoadError('Failed to load PDF. The file may be corrupted.')
  }

  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(1, prev - 1))
  }

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(numPages, prev + 1))
  }

  const handleZoomIn = () => {
    setScale(prev => Math.min(3.0, prev + 0.25))
  }

  const handleZoomOut = () => {
    setScale(prev => Math.max(0.5, prev - 0.25))
  }

  const handleZoomFit = () => {
    setScale(1.0)
  }

  const handlePageInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const page = parseInt(e.target.value)
    if (page >= 1 && page <= numPages) {
      setPageNumber(page)
      // Scroll to the selected page
      setTimeout(() => {
        const pageElement = document.querySelector(`[data-page-number="${page}"]`)
        pageElement?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    }
  }
  
  // Scroll to current page when pageNumber changes (for navigation buttons)
  useEffect(() => {
    if (numPages > 0 && pageNumber > 0) {
      const pageElement = document.querySelector(`[data-page-number="${pageNumber}"]`)
      if (pageElement) {
        pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }, [pageNumber, numPages])

  const handleDownload = () => {
    if (pdfBlob && onDownload) {
      onDownload()
    }
  }

  const handleOpenExternal = () => {
    if (pdfUrl && onOpenExternal) {
      onOpenExternal()
    } else if (pdfUrl) {
      window.open(pdfUrl, '_blank')
    }
  }

  if (error) {
    return (
      <div className={clsx('flex flex-col items-center justify-center h-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg', className)}>
        <ExclamationTriangleIcon className="w-12 h-12 text-red-500 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Compilation Error</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 text-center max-w-md">{error}</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className={clsx('flex flex-col items-center justify-center h-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg', className)}>
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-sm text-gray-600 dark:text-gray-400">Compiling LaTeX document...</p>
      </div>
    )
  }

  if (!pdfBlob || !pdfUrl) {
    return (
      <div className={clsx('flex flex-col h-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden', className)}>
        {/* Header with controls - always show even when no PDF */}
        <div className="relative z-10 flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 shadow-sm">
          <div className="flex items-center gap-2">
            {/* Empty space */}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">No PDF</span>
            </div>
            {/* Zoom Controls - always visible */}
            <div className="flex items-center gap-1 border-l border-gray-300 dark:border-gray-600 pl-2">
              <button
                disabled
                className="p-1.5 text-gray-400 dark:text-gray-600 rounded transition-colors cursor-not-allowed"
                title="Zoom out"
              >
                <MagnifyingGlassMinusIcon className="w-4 h-4" />
              </button>
              <button
                disabled
                className="px-2 py-1 text-xs text-gray-400 dark:text-gray-600 rounded transition-colors cursor-not-allowed"
                title="Reset zoom"
              >
                100%
              </button>
              <button
                disabled
                className="p-1.5 text-gray-400 dark:text-gray-600 rounded transition-colors cursor-not-allowed"
                title="Zoom in"
              >
                <MagnifyingGlassPlusIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        {/* Content area */}
        <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900">
          <DocumentTextIcon className="w-12 h-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No Preview Available</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center max-w-md">
            Compile your LaTeX document to see the PDF preview here
          </p>
        </div>
      </div>
    )
  }

  if (loadError) {
    return (
      <div className={clsx('flex flex-col items-center justify-center h-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg', className)}>
        <ExclamationTriangleIcon className="w-12 h-12 text-orange-500 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Preview Error</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 text-center max-w-md mb-4">{loadError}</p>
        <div className="flex gap-2">
          <button
            onClick={handleDownload}
            className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
          >
            Download PDF
          </button>
          <button
            onClick={handleOpenExternal}
            className="px-3 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
          >
            Open Externally
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={clsx('flex flex-col h-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden', className)}>
      {/* Header with controls */}
      <div className="relative z-10 flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 shadow-sm">
        <div className="flex items-center gap-2">
          {/* PDF Preview text removed */}
        </div>
        
        <div className="flex items-center gap-2">
          {/* Page Navigation */}
          {numPages > 0 ? (
            <div className="flex items-center gap-2 px-2">
              <button
                onClick={goToPrevPage}
                disabled={pageNumber <= 1}
                className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous page"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min="1"
                  max={numPages}
                  value={pageNumber}
                  onChange={handlePageInput}
                  className="w-12 px-1 py-0.5 text-sm text-center border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
                <span className="text-xs text-gray-500 dark:text-gray-400">/ {numPages}</span>
              </div>
              
              <button
                onClick={goToNextPage}
                disabled={pageNumber >= numPages}
                className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next page"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">Loading...</span>
            </div>
          )}

          {/* Zoom Controls */}
          <div className="flex items-center gap-1 border-l border-gray-300 dark:border-gray-600 pl-2">
            <button
              onClick={handleZoomOut}
              className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
              title="Zoom out"
            >
              <MagnifyingGlassMinusIcon className="w-4 h-4" />
            </button>
            <button
              onClick={handleZoomFit}
              className="px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
              title="Reset zoom"
            >
              {Math.round(scale * 100)}%
            </button>
            <button
              onClick={handleZoomIn}
              className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
              title="Zoom in"
            >
              <MagnifyingGlassPlusIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Debug Panel Button - Show if there are errors or warnings */}
          {(compilationErrors.length > 0 || compilationWarnings.length > 0) && onToggleDebugPanel && (
            <div className="flex items-center gap-1 border-l border-gray-300 dark:border-gray-600 pl-2">
              <button
                onClick={onToggleDebugPanel}
                className={clsx(
                  'relative p-1.5 rounded transition-colors',
                  compilationErrors.length > 0
                    ? 'text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20'
                    : 'text-yellow-600 dark:text-yellow-400 hover:text-yellow-700 dark:hover:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
                )}
                title={
                  compilationErrors.length > 0
                    ? `${compilationErrors.length} error(s)${compilationWarnings.length > 0 ? `, ${compilationWarnings.length} warning(s)` : ''}`
                    : `${compilationWarnings.length} warning(s)`
                }
              >
                {compilationErrors.length > 0 ? (
                  <ExclamationTriangleIcon className="w-4 h-4" />
                ) : (
                  <InformationCircleIcon className="w-4 h-4" />
                )}
                {/* Badge with total count - show errors if any, otherwise warnings */}
                {(compilationErrors.length > 0 || compilationWarnings.length > 0) && (
                  <span
                    className={clsx(
                      'absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full flex items-center justify-center',
                      compilationErrors.length > 0
                        ? 'bg-red-500 text-white'
                        : 'bg-yellow-500 text-white'
                    )}
                  >
                    {compilationErrors.length > 0 ? compilationErrors.length : compilationWarnings.length}
                  </span>
                )}
              </button>
            </div>
          )}

          {/* Light/Dark Mode Toggle */}
          <div className="flex items-center gap-1 border-l border-gray-300 dark:border-gray-600 pl-2">
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
              title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDarkMode ? (
                <SunIcon className="w-4 h-4" />
              ) : (
                <MoonIcon className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-1 border-l border-gray-300 dark:border-gray-600 pl-2">
            <button
              onClick={handleDownload}
              className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
              title="Download PDF"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
            </button>
            <button
              onClick={handleOpenExternal}
              className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
              title="Open in new tab"
            >
              <ArrowsPointingOutIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* PDF Viewer - Scrollable with all pages */}
      <div 
        ref={scrollContainerRef}
        className={clsx(
          "flex-1 relative overflow-auto z-0 transition-colors",
          isDarkMode ? "bg-gray-900" : "bg-gray-100"
        )}
      >
        <div className="flex flex-col items-center p-4 gap-4">
          <Document
            file={pdfBlob || pdfUrl || undefined}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-4" />
                <p className="text-sm text-gray-600 dark:text-gray-400">Loading PDF...</p>
              </div>
            }
            error={
              <div className="flex flex-col items-center justify-center py-12">
                <ExclamationTriangleIcon className="w-12 h-12 text-orange-500 mb-4" />
                <p className="text-sm text-gray-600 dark:text-gray-400">Failed to load PDF</p>
              </div>
            }
          >
            {/* Render all pages for scrolling - all pages at full opacity */}
            {Array.from(new Array(numPages), (el, index) => {
              const pageNum = index + 1
              return (
                <div
                  key={`page_${pageNum}`}
                  data-page-number={pageNum}
                  className="mb-4"
                  style={isDarkMode ? {
                    filter: 'invert(1) hue-rotate(180deg)',
                    transition: 'filter 0.2s ease-in-out'
                  } : {
                    transition: 'filter 0.2s ease-in-out'
                  }}
                  onDoubleClick={async (event) => {
                    // Handle double-click for SyncTeX navigation
                    if (!documentId || !onNavigateToSource) {
                      return
                    }
                    
                    const pageElement = event.currentTarget
                    const rect = pageElement.getBoundingClientRect()
                    
                    // Get click coordinates relative to page
                    const clickX = event.clientX - rect.left
                    const clickY = event.clientY - rect.top
                    
                    // PDF coordinates: X is from left, Y is from bottom
                    // react-pdf renders pages with origin at top-left, but PDF coordinates use bottom-left
                    // Note: PDF coordinates are in "points" (1/72 inch), and react-pdf uses CSS pixels
                    // The scale factor converts CSS pixels to PDF points
                    const pageHeight = rect.height
                    const pdfX = clickX / scale
                    // Convert Y from top-left (react-pdf) to bottom-left (PDF coordinates)
                    // PDF Y=0 is at bottom, react-pdf Y=0 is at top
                    const pdfY = (pageHeight - clickY) / scale
                    
                    console.log('[PDFPreview] Coordinate conversion:', {
                      clickX,
                      clickY,
                      pageHeight,
                      scale,
                      pdfX,
                      pdfY,
                      'note': 'PDF coordinates: X from left, Y from bottom (in points)'
                    })
                    
                    console.log('[PDFPreview] Double-click detected:', {
                      page: pageNum,
                      clickX,
                      clickY,
                      pdfX,
                      pdfY,
                      scale,
                      pageHeight,
                      documentId,
                      mainFilePath
                    })
                    
                    try {
                      const result = await latexAPI.synctexLookup(documentId, {
                        page: pageNum,
                        x: pdfX,
                        y: pdfY,
                        main_file_path: mainFilePath
                      })
                      
                      console.log('[PDFPreview] SyncTeX lookup result:', result)
                      
                      if (result.found && result.file_path && result.line !== null) {
                        console.log('[PDFPreview] Calling onNavigateToSource with:', {
                          file_path: result.file_path,
                          line: result.line,
                          column: result.column || 1
                        })
                        onNavigateToSource(
                          result.file_path,
                          result.line,
                          result.column || 1
                        )
                      } else {
                        console.log('[PDFPreview] SyncTeX lookup found no match')
                      }
                    } catch (error) {
                      console.error('[PDFPreview] SyncTeX lookup error:', error)
                    }
                  }}
                >
                  <Page
                    pageNumber={pageNum}
                    scale={scale}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    className="shadow-lg"
                  />
                </div>
              )
            })}
          </Document>
        </div>
      </div>
    </div>
  )
}

export default PDFPreview
